// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Push-to-talk / open-mic transmit logic. Pure and side-effect-free so the rule
 * that decides "is my microphone leaving this device right now?" is unit-tested
 * directly, away from any live MediaStream. The single source of truth for the
 * outgoing audio track's `enabled` flag.
 */
import type { TalkMode } from './types.ts';

export interface TransmitInput {
  mode: TalkMode;
  /** Open-mic only: user has toggled themselves muted. */
  muted: boolean;
  /** PTT only: the talk button / Space key is currently held. */
  held: boolean;
  /** Listen-only join (mic denied or unavailable) — never transmit. */
  listenOnly: boolean;
}

/**
 * Whether the local microphone should be transmitting to peers right now.
 * - listen-only: never.
 * - open mic: transmit unless the user muted.
 * - push-to-talk: transmit only while the button/Space is held.
 */
export function shouldTransmit({ mode, muted, held, listenOnly }: TransmitInput): boolean {
  if (listenOnly) return false;
  if (mode === 'ptt') return held;
  return !muted;
}

/** Human label for the primary mic control, given the current state. */
export function micButtonLabel(mode: TalkMode, transmitting: boolean, listenOnly: boolean): string {
  if (listenOnly) return 'Listen-only';
  if (mode === 'ptt') return transmitting ? 'Release to stop' : 'Hold to talk';
  return transmitting ? 'Mute' : 'Unmute';
}

/** The other mode — used by the mode toggle. */
export function otherMode(mode: TalkMode): TalkMode {
  return mode === 'ptt' ? 'open' : 'ptt';
}
